import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LoaderCircle, TriangleAlert, Ban } from "lucide-react";
import { AI_ACTIONS, formatTimestamp, type AiAction } from "@brainlog/shared";
import { errorMessage, get, post } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { Proposal, ScopePreview, SourceProv } from "./types";
import { ProposalReview } from "./ProposalReview";
import { WritingModelPicker, writingModelFor } from "./WritingModel";

/** Plain names for the kinds of source an action reads. */
const SOURCE_TYPES: Record<string, string> = {
  note: "note",
  selection: "selected text",
  paper_page: "paper page",
  library_item: "paper",
  paper: "paper",
  event: "event",
  task: "task",
  brief: "project overview",
  revision_item: "reviewer request",
  manuscript: "manuscript",
  manuscript_info: "manuscript details",
  mail_thread: "email conversation",
  mail_draft: "email draft",
  guidelines: "guidelines",
  comments: "comments",
  submission: "submission",
  web: "web page",
  workspace: "notebook or project",
};

export function SourceList({ sources }: { sources: SourceProv[] }) {
  if (!sources.length) return <p className="text-xs text-muted-foreground">Nothing to send.</p>;
  const excluded = sources.filter((s) => !s.included).length;
  const truncated = sources.filter((s) => s.truncated).length;
  return (
    <div className="space-y-1">
      {(excluded > 0 || truncated > 0) && (
        <p className="rounded border border-warning/50 bg-warning/10 px-2 py-1 text-xs" role="status">
          Too much text for one request: {excluded ? `${excluded} item(s) left out` : ""}
          {excluded && truncated ? ", " : ""}
          {truncated ? `${truncated} shortened` : ""}. The AI has not read them in full.
        </p>
      )}
      <ul className="max-h-48 space-y-0.5 overflow-y-auto text-xs">
        {sources.map((s) => (
          <li key={s.ref} className="flex gap-1.5">
            <span className="w-7 shrink-0 font-mono text-muted-foreground">{s.ref}</span>
            <span className="min-w-0 flex-1">
              <span className="break-words">{s.title}</span>
              {s.page ? ` — PDF p. ${s.page}${s.pageLabel ? ` (printed ${s.pageLabel})` : ""}` : ""}
              <span className="text-muted-foreground">
                {" "}
                · {SOURCE_TYPES[s.type] ?? s.type.replace(/_/g, " ")} · {s.included ? (s.truncated ? `shortened (${s.chars.toLocaleString()} of ${s.originalChars.toLocaleString()} characters)` : `${s.chars.toLocaleString()} characters`) : "left out (too long)"}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function RunFlow(props: { action?: AiAction; workspaceId: string; params?: unknown; existingId?: string; onBack(): void }) {
  const qc = useQueryClient();
  const me = useMe();
  const [proposalId, setProposalId] = useState<string | null>(props.existingId ?? null);
  const [starting, setStarting] = useState(false);
  const scope = useQuery({
    queryKey: ["ai-scope", props.action, props.workspaceId, JSON.stringify(props.params)],
    queryFn: () => post<ScopePreview>("/api/ai/scope", { action: props.action, workspaceId: props.workspaceId, params: props.params }),
    enabled: !proposalId && !!props.action,
    retry: false,
    staleTime: 0,
  });
  const proposal = useQuery({
    queryKey: ["proposal", proposalId],
    queryFn: () => get<{ proposal: Proposal; applications: { itemKey: string; entityType: string; entityId: string | null }[] }>(`/api/ai/proposals/${proposalId}`),
    enabled: !!proposalId,
    refetchInterval: (q) => (q.state.data && ["queued", "running"].includes(q.state.data.proposal.status) ? 1000 : false),
  });

  const run = async () => {
    setStarting(true);
    try {
      const { proposal } = await post<{ proposal: Proposal }>("/api/ai/proposals", { action: props.action, workspaceId: props.workspaceId, params: props.params, model: props.action ? writingModelFor(props.action) : undefined });
      setProposalId(proposal.id);
      qc.invalidateQueries({ queryKey: ["proposals", props.workspaceId] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setStarting(false);
    }
  };

  if (!proposalId) {
    if (scope.isLoading) return <p className="text-sm text-muted-foreground" role="status">Checking what would be used…</p>;
    if (scope.isError)
      return (
        <div className="space-y-2" role="alert">
          <p className="text-sm text-destructive">{errorMessage(scope.error)}</p>
          <Button size="sm" variant="outline" onClick={props.onBack}>
            Back
          </Button>
        </div>
      );
    const s = scope.data!;
    return (
      <div className="space-y-3">
        <div className="rounded-md border bg-background p-2 text-sm">
          <p>
            <span className="font-medium">Works on:</span> {s.scope}
          </p>
          <p className="mt-1 text-xs">{s.destination.disclosure}</p>
          {s.destination.usesChatgptPlan && <p className="mt-1 text-xs"><Badge variant="outline">Using ChatGPT plan</Badge>{" "}<a href="https://chatgpt.com/settings/usage" target="_blank" rel="noreferrer" className="underline">Manage usage</a></p>}
          {s.destination.isDemo && (
            <p className="mt-1 text-xs">
              <Badge variant="outline" className="border-ai text-ai">
                Practice mode
              </Badge>{" "}
              Results come from simple rules, not from AI. They show how reviewing works; they are not real analysis.
            </p>
          )}
        </div>
        {s.warnings.map((w) => (
          <p key={w} className="text-xs text-muted-foreground">
            {w}
          </p>
        ))}
        {props.action !== "recent_updates" && (
          <div>
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">What will be sent ({s.sources.length})</h4>
            <SourceList sources={s.sources} />
          </div>
        )}
        {!s.availability.available && <p className="text-sm">{s.availability.reason}</p>}
        <WritingModelPicker action={props.action!} />
        <div className="flex gap-2">
          <Button size="sm" onClick={run} disabled={!s.availability.available || starting}>
            {starting ? "Starting…" : `Run ${AI_ACTIONS.labels[props.action!]}`}
          </Button>
          <Button size="sm" variant="ghost" onClick={props.onBack}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  const p = proposal.data?.proposal;
  if (!p) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const cancel = async () => {
    try {
      await post(`/api/ai/proposals/${p.id}/cancel`);
      qc.invalidateQueries({ queryKey: ["proposal", p.id] });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const retry = async () => {
    try {
      const { proposal: np } = await post<{ proposal: Proposal }>(`/api/ai/proposals/${p.id}/retry`);
      setProposalId(np.id);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>{AI_ACTIONS.labels[p.action as AiAction]}</span>
        {p.isDemo && (
          <Badge variant="outline" className="border-ai text-ai">
            Practice mode
          </Badge>
        )}
        <span>
          {p.provider === "chatgpt" && <><Badge variant="outline">Using ChatGPT plan</Badge>{" "}<a href="https://chatgpt.com/settings/usage" target="_blank" rel="noreferrer" className="underline">Manage usage</a>{" "}</>}
          {!p.isDemo && p.provider === "anthropic" ? "· Claude " : !p.isDemo && p.model ? `· ${p.model} ` : ""}· started {formatTimestamp(p.createdAt, me.data?.timezone)}
        </span>
      </div>
      {(p.status === "queued" || p.status === "running") && (
        <div className="flex items-center gap-2 text-sm" role="status" aria-live="polite">
          <LoaderCircle className="size-4 animate-spin" aria-hidden /> {p.status === "queued" ? "Waiting to start…" : "Working… your notes stay editable meanwhile."}
          <Button size="xs" variant="outline" onClick={cancel}>
            Cancel
          </Button>
        </div>
      )}
      {p.status === "failed" && (
        <div className="space-y-2 rounded-md border border-destructive/50 p-2 text-sm" role="alert">
          <p className="flex gap-1.5">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden /> Failed: {p.error}
          </p>
          <p className="text-xs text-muted-foreground">Nothing was changed. Your writing is unaffected.</p>
          <Button size="xs" variant="outline" onClick={retry}>
            Retry
          </Button>
        </div>
      )}
      {(p.status === "blocked" || p.status === "cancelled") && (
        <div className="space-y-2 rounded-md border p-2 text-sm" role="status">
          <p className="flex gap-1.5">
            <Ban className="mt-0.5 size-4 shrink-0" aria-hidden /> {p.status === "cancelled" ? "Cancelled. Run it again to get a new result." : p.error}
          </p>
          <Button size="xs" variant="outline" onClick={retry}>
            Run again
          </Button>
        </div>
      )}
      {p.status === "succeeded" && p.result && <ProposalReview proposal={p} applications={proposal.data!.applications} />}
      <Button size="xs" variant="ghost" onClick={props.onBack}>
        Back
      </Button>
    </div>
  );
}
